import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import ReportHeader from "../../components/reports/ReportHeader";

const fmt = (n) =>
  n != null && n !== "" ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = d.toString().split("T")[0];
  const [y, m, dd] = dt.split("-");
  return `${dd}-${m}-${y}`;
};

const COLUMNS = [
  { key: "sno",                     label: "#",                         align: "center" },
  { key: "branchName",              label: "Branch Name",               align: "left"   },
  { key: "branchCode",              label: "Branch Code",               align: "left"   },
  { key: "region",                  label: "Region",                    align: "left"   },
  { key: "division",                label: "Division",                  align: "left"   },
  { key: "customerName",            label: "Customer Name",             align: "left"   },
  { key: "prospectId",              label: "Prospect ID",               align: "left"   },
  { key: "contactNo",               label: "Contact No",                align: "left"   },
  { key: "loanAccountNo",           label: "Loan Account No",           align: "left"   },
  { key: "disbursedAmount",         label: "Disbursed Amount",          align: "right"  },
  { key: "tenureInMonths",          label: "Tenure",                    align: "center" },
  { key: "rateOfInterest",          label: "ROI %",                     align: "center" },
  { key: "emiAmount",               label: "EMI Amount",                align: "right"  },
  { key: "emiStartDate",            label: "EMI Start Date",            align: "left"   },
  { key: "maturityDate",            label: "Maturity Date",             align: "left"   },
  { key: "emiMonthsPaid",           label: "EMI Months Paid",           align: "center" },
  { key: "overdueEMIs",             label: "Overdue EMIs",              align: "center" },
  { key: "lastPaidDate",            label: "Last Paid Date",            align: "left"   },
  { key: "totalInterestScheduled",  label: "Total Interest Scheduled",  align: "right"  },
  { key: "interestPaid",            label: "Interest Paid",             align: "right"  },
  { key: "outstandingInterest",     label: "Outstanding Interest",      align: "right"  },
  { key: "overdueInterest",         label: "Overdue Interest",          align: "right"  },
  { key: "futureInterest",          label: "Future Interest",           align: "right"  },
  { key: "collectionRate",          label: "Collection Rate %",         align: "center" },
  { key: "isDemised",               label: "Is Demised(Insurance)",     align: "center" },
  { key: "processingFee",           label: "Processing Fee",            align: "right"  },
  { key: "gst",                     label: "GST",                       align: "right"  },
  { key: "insurance",               label: "Insurance",                 align: "right"  },
  { key: "securityDeposit",         label: "Security Deposit",          align: "right"  },
  { key: "loanPurpose",             label: "Loan Purpose",              align: "left"   },
  { key: "aadharNumber",            label: "Aadhar Number",             align: "left"   },
  { key: "panNumber",               label: "Pan Number",                align: "left"   },
  { key: "voterId",                 label: "Voter Id",                  align: "left"   },
  { key: "bankAccountNumber",       label: "Bank Account Number",       align: "left"   },
  { key: "ifscCode",                label: "IFSC Code",                 align: "left"   },
  { key: "bankName",                label: "Bank Name",                 align: "left"   },
  { key: "bankBranch",              label: "Bank Branch",               align: "left"   },
  { key: "employeeName",            label: "Employee Name",             align: "left"   },
  { key: "employeeCode",            label: "Employee Code",             align: "center" },
];

const CURRENCY_KEYS = new Set([
  "disbursedAmount", "emiAmount",
  "totalInterestScheduled", "interestPaid",
  "outstandingInterest", "overdueInterest", "futureInterest",
  "processingFee", "gst", "insurance", "securityDeposit",
]);
const DATE_KEYS = new Set(["emiStartDate", "maturityDate", "lastPaidDate"]);

const EMPTY_SUMMARY = {
  totalActiveLoans: 0,
  parAccounts: 0,
  grandTotalScheduled: 0,
  grandTotalPaid: 0,
  grandTotalOutstanding: 0,
  grandTotalOverdue: 0,
  grandFutureInterest: 0,
  overallCollectionRate: "0.00",
};

const InterestReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [rows,        setRows]        = useState([]);
  const [summary,     setSummary]     = useState(EMPTY_SUMMARY);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [search,      setSearch]      = useState("");
  const [fromDate,    setFromDate]    = useState("");
  const [toDate,      setToDate]      = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (!isSuperAdmin && branchId) params.branchId = branchId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate)   params.toDate   = toDate;

      const res = await axios.get("/api/v2/interest-report", { baseURL: API_BASE_URL, params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setSummary(res.data?.summary || EMPTY_SUMMARY);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setError("Failed to load interest report. Please try again.");
    }
    setLoading(false);
  }, [isSuperAdmin, branchId, fromDate, toDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Client-side filter
  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      (r.customerName   || "").toLowerCase().includes(q) ||
      (r.loanAccountNo  || "").toLowerCase().includes(q) ||
      (r.prospectId     || "").toLowerCase().includes(q) ||
      (r.branchName     || "").toLowerCase().includes(q) ||
      (r.employeeName   || "").toLowerCase().includes(q) ||
      String(r.contactNo || "").includes(q);
    return matchSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Dynamic filtered aggregates
  const filteredPaid        = filtered.reduce((s, r) => s + (Number(r.interestPaid)           || 0), 0);
  const filteredOutstanding = filtered.reduce((s, r) => s + (Number(r.outstandingInterest)    || 0), 0);
  const filteredOverdue     = filtered.reduce((s, r) => s + (Number(r.overdueInterest)        || 0), 0);
  const filteredScheduled   = filtered.reduce((s, r) => s + (Number(r.totalInterestScheduled) || 0), 0);

  // CSV export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => c.label);
    const csvRows = [
      headers.join(","),
      ...filtered.map((r) =>
        COLUMNS.map((c) => {
          const val = String(r[c.key] ?? "").replace(/"/g, '""');
          return val.includes(",") ? `"${val}"` : val;
        }).join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "interest_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (r, col) => {
    const val = r[col.key];

    if (col.key === "sno") return <span className="text-ink-400">{val}</span>;

    if (DATE_KEYS.has(col.key)) return fmtDate(val);

    if (CURRENCY_KEYS.has(col.key)) {
      if (val === "" || val == null) return "—";
      const isRed   = col.key === "overdueInterest";
      const isGreen = col.key === "interestPaid";
      const textColor = isRed ? "text-rose-600 font-bold" : isGreen ? "text-emerald-600 font-bold" : "";
      return <span className={textColor}>₹{fmt(val)}</span>;
    }

    if (col.key === "collectionRate") {
      const rate = parseFloat(val) || 0;
      const colorClass = rate >= 90 ? "bg-emerald-500" : rate >= 70 ? "bg-amber-500" : "bg-rose-500";
      const textClass = rate >= 90 ? "text-emerald-600" : rate >= 70 ? "text-amber-600" : "text-rose-600";
      return (
        <div className="flex items-center gap-2 min-w-[130px]">
          <div className="h-1.5 flex-1 bg-ink-100 rounded-full overflow-hidden">
            <div className={`h-full ${colorClass} rounded-full`} style={{ width: `${Math.min(rate, 100)}%` }} />
          </div>
          <span className={`font-bold text-xs min-w-[42px] ${textClass}`}>
            {rate.toFixed(1)}%
          </span>
        </div>
      );
    }

    if (col.key === "customerName") return <span className="font-semibold text-ink-900">{val || "—"}</span>;
    if (col.key === "loanAccountNo") return <code className="text-xs font-mono text-primary font-bold">{val || "—"}</code>;
    if (col.key === "overdueEMIs" && val > 0) return <span className="text-rose-600 font-bold">{val}</span>;

    return val !== "" && val != null ? val : "—";
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        filters={
          <>
            {/* From Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="From Date"
                selected={fromDate}
                onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* To Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="To Date"
                selected={toDate}
                onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* Rows/Page */}
            <div className="relative min-w-[100px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Rows/Page
              </label>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-1">
              <Button onClick={loadData} variant="secondary" className="h-[38px] px-3 font-semibold text-xs">
                Apply Filters
              </Button>
            </div>

            {/* Search Input */}
            <div className="min-w-[240px] mt-1">
              <input
                placeholder="Search customer / account / branch..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-3.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm h-[38px] px-4 font-bold"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading interest data..." />}

      {/* Summary Cards */}
      <div className="grid grid-cols-7 gap-2.5 mb-4">
        {[
          { label: "Total Active Loans", value: summary.totalActiveLoans, bg: "bg-[#DCEBFF]", border: "border-[#2563EB]/25", accent: "text-[#2563EB]" },
          { label: "PAR Accounts", value: summary.parAccounts, bg: "bg-[#E9DFFF]", border: "border-[#7C3AED]/25", accent: "text-[#7C3AED]" },
          { label: "Interest Scheduled", value: `₹${fmt(summary.grandTotalScheduled)}`, bg: "bg-[#FFF0C7]", border: "border-[#D97706]/25", accent: "text-[#D97706]" },
          { label: "Interest Collected", value: `₹${fmt(summary.grandTotalPaid)}`, bg: "bg-[#D9F3F7]", border: "border-[#0891B2]/25", accent: "text-[#0891B2]" },
          { label: "Outstanding Interest", value: `₹${fmt(summary.grandTotalOutstanding)}`, bg: "bg-[#DDF5E6]", border: "border-[#16A34A]/25", accent: "text-[#16A34A]" },
          { label: "Overdue Interest", value: `₹${fmt(summary.grandTotalOverdue)}`, bg: "bg-[#DCEBFF]", border: "border-[#2563EB]/25", accent: "text-[#2563EB]" },
          { label: "Collection Rate", value: `${summary.overallCollectionRate}%`, bg: "bg-[#E9DFFF]", border: "border-[#7C3AED]/25", accent: "text-[#7C3AED]" },
        ].map((c, idx) => (
          <div key={idx} className={`${c.bg} ${c.border} border py-2 px-2.5 rounded-xl shadow-xs flex flex-col justify-center min-h-[56px]`}>
            <span className={`text-[10px] font-bold ${c.accent} uppercase tracking-wider mb-0.5 truncate`} title={c.label}>{c.label}</span>
            <span className={`text-sm md:text-base font-black truncate ${c.accent}`}>{c.value}</span>
          </div>
        ))}
      </div>

      {/* Interest Flow Visual */}
      <div className="bg-white py-2.5 px-4 rounded-xl border border-border-light shadow-sm mb-4">
        <div className="font-bold text-[11px] text-ink-800 uppercase tracking-wider mb-2 flex items-center gap-2">
          <i className="fa-solid fa-chart-pie text-primary"></i>
          Interest Flow Overview
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 items-center">
          {[
            { label: "Scheduled", amount: summary.grandTotalScheduled, color: "bg-blue-600 text-blue-600", pct: 100 },
            { label: "Collected", amount: summary.grandTotalPaid, color: "bg-emerald-600 text-emerald-600",
              pct: summary.grandTotalScheduled > 0 ? (summary.grandTotalPaid / summary.grandTotalScheduled * 100) : 0 },
            { label: "Outstanding", amount: summary.grandTotalOutstanding, color: "bg-amber-500 text-amber-500",
              pct: summary.grandTotalScheduled > 0 ? (summary.grandTotalOutstanding / summary.grandTotalScheduled * 100) : 0 },
            { label: "Overdue",     amount: summary.grandTotalOverdue, color: "bg-rose-600 text-rose-600",
              pct: summary.grandTotalScheduled > 0 ? (summary.grandTotalOverdue / summary.grandTotalScheduled * 100) : 0 },
            { label: "Future",      amount: summary.grandFutureInterest, color: "bg-purple-600 text-purple-600",
              pct: summary.grandTotalScheduled > 0 ? (summary.grandFutureInterest / summary.grandTotalScheduled * 100) : 0 },
          ].map((item, idx) => (
            <div key={idx} className="flex flex-col justify-center">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">{item.label}</span>
                <span className={`text-[10px] font-bold ${item.color.split(' ')[1]}`}>{item.pct.toFixed(1)}%</span>
              </div>
              <span className={`text-xs md:text-sm font-bold ${item.color.split(' ')[1]}`}>₹{fmt(item.amount)}</span>
              <div className="h-1 bg-ink-100 rounded-full overflow-hidden mt-1">
                <div className={`h-full ${item.color.split(' ')[0]} rounded-full`} style={{ width: `${Math.min(item.pct, 100).toFixed(1)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filtered aggregates when searching */}
      {search && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Scheduled", value: `₹${fmt(filteredScheduled)}`, color: "text-blue-600 bg-blue-50/20", sub: `Filtered (${filtered.length})` },
            { label: "Paid", value: `₹${fmt(filteredPaid)}`, color: "text-emerald-600 bg-emerald-50/20", sub: "Interest Collected" },
            { label: "Pending", value: `₹${fmt(filteredOutstanding)}`, color: "text-amber-500 bg-amber-50/20", sub: "Outstanding Interest" },
            { label: "Overdue", value: `₹${fmt(filteredOverdue)}`, color: "text-rose-600 bg-rose-50/20", sub: "Overdue Interest" },
          ].map((c, idx) => (
            <div key={idx} className="bg-white p-4 rounded-2xl border border-border-light shadow-sm flex flex-col justify-between">
              <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1">{c.sub}</span>
              <span className={`text-base font-black ${c.color.split(' ')[0]}`}>{c.value}</span>
              <span className="text-[9px] text-ink-400 mt-1">{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4">
          {error}
        </div>
      )}

      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table className="min-w-[2400px]">
          <Table.Head>
            <tr>
              {COLUMNS.map((c) => (
                <Table.HeadCell key={c.key} align={c.align} className="whitespace-nowrap">
                  {c.label}
                </Table.HeadCell>
              ))}
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-10">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader size="md" />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : paginated.length === 0 ? (
              <Table.EmptyRow colSpan={COLUMNS.length} message="No interest records found." />
            ) : (
              paginated.map((r, i) => {
                const hasOverdue = r.overdueInterest > 0;
                return (
                  <Table.Row key={i} className={hasOverdue ? "border-l-2 border-rose-500" : "border-l-2 border-primary"}>
                    {COLUMNS.map((col) => (
                      <Table.Cell key={col.key} align={col.align} className="whitespace-nowrap">
                        {renderCell(r, col)}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <span className="text-xs text-ink-500">
              Showing {paginated.length} of {filtered.length} records {filtered.length !== rows.length && `(filtered from ${rows.length})`}
            </span>
            <div className="flex gap-1 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-xs text-ink-500 px-3 font-semibold">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default InterestReport;
